import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Loader2, Linkedin, Instagram, ExternalLink, Smartphone } from 'lucide-react';
import { loginWithGoogle } from '../services/authService';
import { User } from '../types';

interface LoginProps {
  onLoginSuccess: (user: User) => void;
}

// Detect if running in an embedded browser (WebView)
const isEmbeddedBrowser = (): boolean => {
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
  
  // Check for iOS WebView
  const isIOSWebView = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(ua) ||
                       // Instagram, Facebook, Twitter, LinkedIn, etc.
                       /FBAN|FBAV|Instagram|Twitter|Line|Snapchat|LinkedIn/i.test(ua);
  
  // Check for Android WebView
  const isAndroidWebView = /wv|WebView|; wv\)/i.test(ua);
  
  return isIOSWebView || isAndroidWebView;
};

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEmbeddedWarning, setShowEmbeddedWarning] = useState(false);

  useEffect(() => {
    // Check if in embedded browser on mount
    if (isEmbeddedBrowser()) {
      setShowEmbeddedWarning(true);
    }
  }, []);

  const getErrorMessage = (err: any) => {
    if (err?.message?.includes('canceló')) return null;
    if (err?.message?.includes('muchos intentos')) return 'Demasiados intentos fallidos. Por favor espera unos minutos.';
    if (err?.message === 'EMBEDDED_BROWSER_ERROR') return 'EMBEDDED_BROWSER';
    return 'Ocurrió un error inesperado. Por favor intenta nuevamente.';
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // signInWithRedirect will redirect the user to Google login
      // The result will be handled when the user returns to the app
      await loginWithGoogle();
    } catch (err: any) {
      console.error("Google Login failed", err);
      setIsLoading(false);
      const errorMsg = getErrorMessage(err);
      if (errorMsg) {
        setError(errorMsg);
        // Show embedded browser warning if this is the error
        if (errorMsg === 'EMBEDDED_BROWSER') {
          setShowEmbeddedWarning(true);
        }
      }
    }
  };

  const openInBrowser = () => {
    // Get current URL
    const currentUrl = window.location.href;
    // Open in external browser
    window.open(currentUrl, '_system');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-center items-center p-4 relative overflow-hidden">
      
      {/* Background Ambient Effects */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-sky-200/40 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-emerald-100/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-teal-200/40 rounded-full blur-3xl" />
      </div>

      <div className="bg-white p-8 rounded-3xl shadow-xl border border-slate-100 w-full max-w-md text-center relative overflow-hidden z-10">
        
        {/* Decorative elements */}
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-sky-100 rounded-full blur-3xl opacity-50 pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-emerald-100 rounded-full blur-3xl opacity-50 pointer-events-none" />
        
        {/* Free Badge */}
        <div className="absolute top-6 right-6">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-600 text-[10px] font-bold uppercase tracking-widest">
            100% Gratuito
          </div>
        </div>
        
        {/* Logo */}
        <div className="mb-6 flex justify-center relative z-10">
          <div className="relative group">
            {/* Glow effect */}
            <div className="absolute -inset-2 bg-gradient-to-r from-sky-400 via-emerald-400 to-teal-400 rounded-3xl opacity-30 group-hover:opacity-50 blur-2xl transition-opacity duration-500"></div>
            {/* Logo container */}
            <div className="relative w-20 h-20 rounded-3xl bg-gradient-to-br from-sky-400 via-sky-500 to-emerald-500 p-1 shadow-2xl shadow-sky-300/50 transform hover:scale-105 transition-transform duration-300">
              <div className="w-full h-full bg-white rounded-[22px] flex items-center justify-center overflow-hidden">
                <img 
                  src="/logo-empatia.png" 
                  alt="Clara AI by Empatía" 
                  className="w-14 h-14 object-contain"
                />
              </div>
            </div>
          </div>
        </div>

        <h1 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-b from-slate-900 via-slate-700 to-slate-500 mb-1 relative z-10 pb-1">Clara AI</h1>
        <div className="text-sm font-medium text-slate-500 mb-8 flex items-center justify-center gap-2 relative z-10">
          <span>by</span>
          <a href="https://masempatia.com" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:text-sky-700 hover:underline">Empatía</a>
          <div className="flex items-center gap-1.5 border-l border-slate-300 pl-2 ml-1">
            <a 
              href="https://www.linkedin.com/in/marielasporn" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-sky-600 transition-colors"
              title="LinkedIn"
            >
              <Linkedin className="w-3.5 h-3.5" />
            </a>
            <a 
              href="https://instagram.com/masempatia.ok" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-pink-500 transition-colors"
              title="Instagram"
            >
              <Instagram className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        <p className="text-slate-600 mb-8 leading-relaxed relative z-10">
          Ingresa para humanizar tus comunicaciones de salud.
        </p>

        {/* Embedded Browser Warning - Always visible on iOS in-app browsers */}
        {showEmbeddedWarning && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl relative z-10 animate-in slide-in-from-top-2">
            <div className="flex items-start gap-3 mb-3">
              <Smartphone className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-left flex-1">
                <h3 className="font-semibold text-amber-900 text-sm mb-1">
                  ⚠️ Navegador no compatible
                </h3>
                <p className="text-xs text-amber-700 leading-relaxed">
                  Google no permite iniciar sesión desde navegadores integrados (como Instagram, Facebook, Twitter, etc.).
                </p>
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-amber-200 mb-3">
              <p className="text-xs font-semibold text-slate-700 mb-2">📱 Usuarios de iPhone:</p>
              <ol className="text-xs text-slate-600 space-y-1.5 pl-4">
                <li className="leading-relaxed">
                  <strong>Opción 1:</strong> Toca los 3 puntos (•••) abajo a la derecha → "Abrir en Safari"
                </li>
                <li className="leading-relaxed">
                  <strong>Opción 2:</strong> Copia esta URL y pégala en Safari
                </li>
                <li className="leading-relaxed">
                  <strong>Opción 3:</strong> Abre Safari y escribe: <span className="font-mono bg-slate-100 px-1 rounded">clara-ai.web.app</span>
                </li>
              </ol>
            </div>
            <button
              onClick={() => {
                // Copy URL to clipboard
                const url = window.location.href;
                navigator.clipboard.writeText(url).then(() => {
                  alert('✅ URL copiada. Ahora abre Safari y pégala en la barra de direcciones.');
                }).catch(() => {
                  // Fallback for older browsers
                  alert(`Copia esta URL y ábrela en Safari:\n\n${url}`);
                });
              }}
              className="w-full flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-all text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              Copiar URL para Safari
            </button>
          </div>
        )}

        {/* Error Alert */}
        {error && error !== 'EMBEDDED_BROWSER' && (
          <div className="mb-6 p-3 bg-red-50 border border-red-100 rounded-xl flex items-start gap-2 text-red-600 text-sm animate-in slide-in-from-top-2 relative z-10">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p>{error}</p>
          </div>
        )}

        <button
          onClick={handleGoogleLogin}
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-3 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold py-3 px-4 rounded-xl transition-all active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed relative z-10 shadow-sm hover:shadow-md"
        >
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin text-sky-500" />
          ) : (
            <>
              <div className="p-1 bg-white rounded">
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
              </div>
              <span>Continuar con Google</span>
            </>
          )}
        </button>

        <p className="mt-6 text-xs text-slate-400 relative z-10">
          Al continuar, aceptás nuestros{' '}
          <Link 
            to="/terms"
            className="text-sky-500 hover:text-sky-600 underline underline-offset-2"
          >
            términos y condiciones
          </Link>
          {' '}y{' '}
          <Link 
            to="/privacy"
            className="text-sky-500 hover:text-sky-600 underline underline-offset-2"
          >
            política de privacidad
          </Link>.
        </p>
        
        <p className="mt-8 text-xs text-slate-400 relative z-10">
          Versión 1.0.0 • © {new Date().getFullYear()} Empatía
        </p>
      </div>
    </div>
  );
};

export default Login;